import React from 'react';
import PropTypes from 'prop-types';
import CustomField from './CustomField';
import registeredComponent from 'utils/registeredComponent';
import CountrySelect from '../shared/CountrySelect';
import StateSelect from '../shared/StateSelect';
import {
  findCountry,
  getStates,
  getZipCodeDisplayName,
  getStateDisplayName,
  getZipCodeRequired
} from 'utils/i18n';

class AddressField extends CustomField {
  constructor() {
    super();

    this.state = {
      value: undefined
    };
  }

  componentDidMount() {
    const {value = {}} = this.props;

    this.setState({value});
  }

  static propTypes = {
    ...CustomField.propTypes,
    resource: PropTypes.string,
    value: PropTypes.shape({
      address_1: PropTypes.string,
      address_2: PropTypes.string,
      country: PropTypes.string, // For legacy purposes, this is a country's display name...
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string
    }),
    errors: PropTypes.shape({
      address_1: PropTypes.bool,
      address_2: PropTypes.bool,
      country: PropTypes.bool, // For legacy purposes, this is a country's display name...
      city: PropTypes.bool,
      state: PropTypes.bool,
      zip: PropTypes.bool
    }),
    map_to: PropTypes.oneOf([
      ...CustomField.MAPPINGS,
      'HOUSEHOLD'
    ]),
    hidden_subfields: PropTypes.arrayOf(PropTypes.string) // names of subfields to hide
  };

  static defaultProps = {
    ...CustomField.defaultProps,
    hidden_subfields: [],
    readonly: false,
    errors: {}
  };

  updateSubfield(subfield) {
    const {value = {}} = this.state;

    this.setState({
      value: {
        ...value,
        ...subfield
      }
    })
  }

  country() {
    const {value = {}} = this.state;

    return findCountry({label: value.country});
  }

  hasStates() {
    const country = this.country();

    return !!country && getStates(country).some(x=>x);
  }

  fieldName(subfield) {
    const {resource} = this.props;
    const mappedSubfieldName = [this.mappedFieldName(), subfield].filter(x=>x).join('_');

    return `${resource}[${mappedSubfieldName}]`;
  }

  renderEdit() {
    const {
      errors,
      hidden_subfields,
      readonly,
      required
    } = this.props;

    const {value = {}} = this.state;

    const country = this.country();

    return <div className={'sa-form-subfield-group'}>
      <div className={'sa-form-subfield-group-body'}>
        <div className={'sa-form-subfield-grouping'}>
          <CustomField.Subfield dataFlavor={'address.address_1'}
                                name={this.fieldName('address_1')}
                                prompt={'Address Line 1'}
                                hasError={errors.address_1}
                                hidden={hidden_subfields.includes('address_1')}
                                required={required}
          >
            <input type={'text'}
                   autoComplete='address-line1'
                   value={value.address_1}
                   className={`form-control form-control-sm ${errors.address_1 ? 'sa-form-error-field' : ''}`}
                   onChange={(event) => this.updateSubfield({address_1: event.target.value})}
                   readOnly={readonly}

            />
          </CustomField.Subfield>

          <CustomField.Subfield dataFlavor={'address.address_2'}
                                name={this.fieldName('address_2')}
                                prompt={'Address Line 2/Apt/Suite'}
                                hasError={errors.address_2}
                                hidden={hidden_subfields.includes('address_2')}
          >
            <input type={'text'}
                   autoComplete='address-line2'

                   value={value.address_2}
                   className={`form-control form-control-sm ${errors.address_2 ? 'sa-form-error-field' : ''}`}
                   onChange={(event) => this.updateSubfield({address_2: event.target.value})}
                   readOnly={readonly}
            />
          </CustomField.Subfield>

          <CustomField.Subfield dataFlavor={'address.country'}
                                autoComplete='country'
                                name={this.fieldName('country')}
                                prompt={'Country'}
                                className={'sa-form-country-field'}
                                hasError={errors.country}
                                hidden={hidden_subfields.includes('country')}
                                required={required}
          >
            <CountrySelect value={value.country}
                           getOptionValue={(c) => c.label}
                           className={errors.country ? 'sa-form-error-field' : ''}
                           onChange={(c) => this.updateSubfield({ country: c ? c.value : '', state: '' })}
                           clearable={true}
                           disabled={readonly}
            />
          </CustomField.Subfield>

          <CustomField.Subfield dataFlavor={'address.city'}
                                name={this.fieldName('city')}
                                prompt={'City'}
                                hasError={errors.city}
                                hidden={hidden_subfields.includes('city')}
                                required={required}
          >
            <input type={'text'}
                   autoComplete='address-level2'
                   value={value.city}
                   className={`form-control form-control-sm ${errors.city ? 'sa-form-error-field' : ''}`}
                   onChange={(event) => this.updateSubfield({city: event.target.value})}
                   readOnly={readonly}
            />
          </CustomField.Subfield>

          <CustomField.Subfield dataFlavor={'address.state'}
                                name={this.fieldName('state')}
                                prompt={getStateDisplayName(country)}
                                hasError={errors.state}
                                required={required && this.hasStates()}
                                hidden={hidden_subfields.includes('state')}
          >
            {!!this.hasStates() ?
              <StateSelect value={value.state}
                           autoComplete='address-level1'
                           className={errors.state ? 'sa-form-error-field' : ''}
                           onChange={(s) => this.updateSubfield({ state: s ? s.value : '' })}
                           clearable={true}
                           country={country}
                           disabled={readonly}
              />
              :
              <input type={'text'}
                     autoComplete='address-level1'
                     value={value.state}
                     className={`form-control form-control-sm ${errors.state ? 'sa-form-error-field' : ''}`}
                     onChange={(event) => this.updateSubfield({state: event.target.value})}
                     readOnly={readonly}
              />
            }
          </CustomField.Subfield>

          <CustomField.Subfield dataFlavor={'address.zip'}
                                name={this.fieldName('zip')}
                                hasError={errors.zip}
                                prompt={getZipCodeDisplayName(country)}
                                hidden={hidden_subfields.includes('zip')}
                                required={required && getZipCodeRequired(country)}
          >
            <input type={'text'}
                   autoComplete='postal-code'
                   value={value.zip}
                   className={`form-control form-control-sm ${errors.zip ? 'sa-form-error-field' : ''}`}
                   onChange={(event) => this.updateSubfield({zip: event.target.value})}
                   readOnly={readonly}
            />
          </CustomField.Subfield>
        </div>
      </div>
    </div>;
  }
}

export default registeredComponent("AddressField")(AddressField);
